import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronsUpDown } from "lucide-react";

import AccountMenu from "../AccountMenu/AccountMenu";
import ClubLogo from "../ClubLogo";
import { useTranslation } from "../../context/Internationalization";
import { getStorage } from "../../auth/utils";
import {
  ACCOUNT_SETTINGS_ROUTE,
  NOTIFICATIONS_ROUTE,
  ROOT_ROUTE,
} from "../../routes";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../shadcn/components/ui/avatar";
import { Badge } from "../../shadcn/components/ui/badge";
import { Button } from "../../shadcn/components/ui/button";
import { Separator } from "../../shadcn/components/ui/separator";
import { SidebarTrigger } from "../../shadcn/components/ui/sidebar";

// Mobile header (below 768px, where the sidebar is a sheet).
const TopBar = ({ handleLogout, invitations, user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const impersonate = getStorage().getItem("impersonate");
  const hasClub = Boolean(user?.club?._id);
  const initials = `${(user?.firstName || "").charAt(0)}${(
    user?.lastName || ""
  ).charAt(0)}`;

  return (
    <header
      // Sits below inline page banners and the sticky impersonation banner.
      // Mobile header of the @shadcndesign app-shell-4 block.
      className="fixed inset-x-0 top-[calc(var(--page-top-offset,0px)+var(--impersonation-banner-height,0px))] z-[1100] flex h-16 shrink-0 items-center gap-2 border-b border-border bg-background px-4 md:hidden"
    >
      <SidebarTrigger
        className="-ml-1"
        aria-label={t("menu")}
        title={t("menu")}
      />
      <Separator orientation="vertical" className="mr-2 h-4" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t("club")}
        onClick={() => navigate(ROOT_ROUTE)}
      >
        <ClubLogo club={user?.club} />
      </Button>
      <div className="grow" />
      {hasClub && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={
            invitations > 0
              ? `${t("notifications")} (${invitations})`
              : t("notifications")
          }
          title={t("notifications")}
          className="relative"
          onClick={() => navigate(NOTIFICATIONS_ROUTE)}
        >
          <Bell aria-hidden="true" />
          {invitations > 0 && (
            <Badge
              variant="destructive"
              aria-hidden="true"
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 tabular-nums"
            >
              {invitations}
            </Badge>
          )}
        </Button>
      )}
      <AccountMenu
        trigger={
          <Button type="button" variant="ghost" className="h-10 gap-1 px-1.5" />
        }
        triggerProps={{ "aria-label": t("open_account_menu") }}
        onOpenSettings={() => navigate(ACCOUNT_SETTINGS_ROUTE)}
        onLogout={handleLogout}
        showSettings={!impersonate}
      >
        <Avatar aria-hidden="true" className="size-8">
          <AvatarImage src={user?.photo || undefined} alt="" />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <ChevronsUpDown aria-hidden="true" className="text-muted-foreground" />
      </AccountMenu>
    </header>
  );
};

export default TopBar;
